import { Box, Paper, styled } from "@mui/material";
import type { Theme } from "@mui/material/styles";

interface LabelProps {
  selected?: boolean;
}

interface MainContentProps extends LabelProps {
  accentColor?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// BaseNode — canvas card for every rule node
// ─────────────────────────────────────────────────────────────────────────────
//
// Layout (matches the prototype's NODE_CFG card pattern):
//
//   ┌──────────────────────────────┐
//   │ [icon] TYPE LABEL            │  <-- BaseNodeLabelContainer (now inside)
//   │                              │
//   │ Bold node title              │  <-- BaseNodeMainContent
//   │ Optional subtitle / config   │
//   │                              │
//   └──────────────────────────────┘
//
// Soft multi-layer shadow for depth, generous padding, brand-color top accent
// rail when the node is selected, smooth hover transition. The type label
// ALWAYS rides the top of the card — no floating-pill-above.
// ─────────────────────────────────────────────────────────────────────────────

export const BaseNodeContainer = styled(Box)(() => ({
	position: "relative",
	width: 300,
	// Allow action buttons (top-right "+" / bottom-right trash) to overlap
	// past the node tile edge.
	overflow: "visible",
}));

// Type-label strip at the TOP of the card. Soft tinted background (uses
// accent color at low alpha) so the type identity reads at any zoom.
export const BaseNodeLabelContainer = styled(Box, {
	shouldForwardProp: (prop) => prop !== "selected" && prop !== "accentColor",
})<LabelProps & { accentColor?: string }>(({ theme, accentColor }) => ({
	display: "flex",
	alignItems: "center",
	gap: theme.spacing(0.875),
	paddingLeft: theme.spacing(2),
	paddingRight: theme.spacing(2),
	paddingTop: theme.spacing(1.5),
	paddingBottom: theme.spacing(1),
	borderTopLeftRadius: 14,
	borderTopRightRadius: 14,
	// Soft tinted strip in the node-type's color so identity is obvious at zoom.
	background: accentColor ? `${accentColor}12` : "transparent",
	borderBottom: `1px solid ${accentColor ? `${accentColor}24` : "rgba(13, 31, 48, 0.06)"}`,
	"& .MuiTypography-caption": {
		textTransform: "uppercase",
		letterSpacing: "0.10em",
		fontWeight: 700,
		fontSize: 11,
	},
}));

export const BaseNodeMainContent = styled(Paper, {
	shouldForwardProp: (prop) => prop !== "selected" && prop !== "accentColor",
})<MainContentProps>(({ theme, selected, accentColor }) => ({
	position: "relative",
	padding: 0,
	borderRadius: 14,
	border: selected
		? `2px solid ${accentColor ?? "#02D5AB"}`
		: `1.5px solid ${accentColor ? `${accentColor}33` : "rgba(13, 31, 48, 0.10)"}`,
	background: "#FFFFFF",
	// Multi-layer prototype-style shadow with a brand-tinted glow halo when selected.
	boxShadow: selected
		? `0 0 0 5px ${accentColor ? `${accentColor}1F` : "rgba(2, 213, 171, 0.12)"}, 0 10px 28px rgba(13, 31, 48, 0.12), 0 2px 6px rgba(13, 31, 48, 0.08)`
		: "0 8px 24px rgba(13, 31, 48, 0.08), 0 2px 6px rgba(13, 31, 48, 0.05), 0 0 0 1px rgba(13, 31, 48, 0.02)",
	width: "100%",
	// No clipping — corner action buttons (trash top-right, + bottom-right)
	// are anchored to the BaseNodeContainer and need to overlap past the
	// BaseNodeMainContent border.
	overflow: "visible",
	transition: "border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
	"&:hover": {
		transform: "translateY(-1px)",
		boxShadow: selected
			? `0 0 0 5px ${accentColor ? `${accentColor}29` : "rgba(2, 213, 171, 0.18)"}, 0 14px 32px rgba(13, 31, 48, 0.14), 0 4px 10px rgba(13, 31, 48, 0.08)`
			: "0 14px 32px rgba(13, 31, 48, 0.12), 0 4px 10px rgba(13, 31, 48, 0.08)",
	},
	// Top accent rail removed per design — node identity now reads from the
	// tinted icon tile + label strip rather than a colored bar.
}));

// Padded body region for the title + config content.
export const BaseNodeBody = styled(Box)(({ theme }) => ({
	paddingLeft: theme.spacing(2),
	paddingRight: theme.spacing(2),
	paddingTop: theme.spacing(1.5),
	paddingBottom: theme.spacing(2),
}));
